import { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";

interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = "Seleccionar...",
  disabled = false,
  className = "",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const displayText =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
      ? selectedOptions[0].label
      : `${selectedOptions.length} seleccionados`;

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full appearance-none bg-white border-2 border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 font-medium shadow-sm 
          transition-all duration-200 
          hover:border-gray-300 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
          disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-200 
          cursor-pointer flex items-center justify-between min-h-[42px]
        `}
      >
        <div className="flex items-center gap-2 flex-wrap flex-1">
          {selectedOptions.length > 0 && selectedOptions.length <= 2 ? (
            selectedOptions.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
              >
                {opt.label}
                <button
                  type="button"
                  onClick={(e) => handleRemove(opt.value, e)}
                  className="hover:bg-blue-100 rounded p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          ) : (
            <span className={selectedOptions.length > 0 ? "text-gray-900" : "text-gray-500"}>
              {displayText}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-lg bg-white shadow-lg border border-gray-200 py-1 max-h-60 overflow-auto">
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            const isAllOption = option.value === "all";
            
            return (
              <div
                key={option.value}
                className={`
                  w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 flex items-center gap-2 cursor-pointer
                  ${
                    isSelected
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-900 hover:bg-gray-50"
                  }
                  ${isAllOption ? "border-b border-gray-200 mb-1 pb-2" : ""}
                `}
              >
                {!isAllOption && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option.value);
                    }}
                    className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 cursor-pointer ${
                      isSelected
                        ? "border-blue-600 bg-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                )}
                <span
                  onClick={() => {
                    if (isAllOption) {
                      // Clear selection and close
                      onChange([]);
                      setIsOpen(false);
                    } else {
                      // Select only this building and close
                      onChange([option.value]);
                      setIsOpen(false);
                    }
                  }}
                  className="flex-1"
                >
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
